/* ─── UserMenu ───
   The accent follows the host app bar's (white-labelled org pages set
   --appbar-accent-rgb), falling back to Carousel purple anywhere else. */

/* ── User avatar menu ── */
.userMenu {
  --_accent-rgb: var(--appbar-accent-rgb, 178, 75, 243);
  position: relative;
  margin-left: var(--nexus-space-xs);
}

.avatarBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--nexus-radius-full);
  border: 1px solid var(--nexus-border);
  background: var(--nexus-surface);
  cursor: pointer;
  overflow: hidden;
  transition:
    border-color 200ms ease,
    box-shadow 200ms ease,
    transform 120ms ease;
}

.avatarBtn:hover {
  border-color: rgba(var(--_accent-rgb), 0.5);
  box-shadow: 0 0 12px -3px rgba(var(--_accent-rgb), 0.35);
}

.avatarBtn:active {
  transform: scale(0.92);
}

.avatarImg {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--nexus-radius-full);
}

.avatarFallback {
  font-size: var(--nexus-text-sm);
  font-weight: var(--nexus-weight-semibold);
  color: var(--nexus-text);
  line-height: 1;
}

.avatarIcon {
  color: var(--nexus-text);
}

/* ── Dropdown ── */
.dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 180px;
  max-width: 280px;
  background: var(--nexus-bg);
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-surface);
  padding: var(--nexus-space-xs);
  display: flex;
  flex-direction: column;
  gap: 1px;
  box-shadow: var(--nexus-shadow-dropdown);
  z-index: 500;
  animation: userMenuFadeIn 120ms ease-out;
}

@keyframes userMenuFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdownHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nexus-space-sm);
  padding: var(--nexus-space-sm) var(--nexus-space-md);
}

.dropdownName {
  font-size: var(--nexus-text-sm);
  font-weight: var(--nexus-weight-semibold);
  color: var(--nexus-text);
}

.dropdownItem {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-sm);
  width: 100%;
  padding: var(--nexus-space-sm) var(--nexus-space-md);
  border-radius: var(--nexus-radius-sm);
  color: var(--nexus-text-secondary);
  font-size: var(--nexus-text-sm);
  font-weight: var(--nexus-weight-medium);
  text-decoration: none;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--nexus-transition-fast), color var(--nexus-transition-fast);
}

.dropdownItem:hover {
  background: var(--nexus-surface-hover);
  color: var(--nexus-text);
  text-decoration: none;
}

.dropdownDivider {
  height: 1px;
  background: var(--nexus-border);
  margin: var(--nexus-space-xs) 0;
}

/* ── Organization section in dropdown ── */

.dropdownSectionLabel {
  padding: var(--nexus-space-xs) var(--nexus-space-md);
  font-family: var(--nexus-font-sans);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--nexus-text-muted);
}

.dropdownOrgBlock {
  padding: var(--nexus-space-xs) var(--nexus-space-md);
}

.dropdownOrgHeader {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-sm);
  margin-bottom: var(--nexus-space-2xs);
}

.dropdownOrgLogo {
  width: 22px;
  height: 22px;
  border-radius: var(--nexus-radius-xs);
  object-fit: cover;
  flex-shrink: 0;
}

.dropdownOrgFallback {
  width: 22px;
  height: 22px;
  border-radius: var(--nexus-radius-xs);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-family: var(--nexus-font-sans);
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, var(--nexus-purple), var(--nexus-pink));
}

.dropdownOrgName {
  font-family: var(--nexus-font-sans);
  font-size: var(--nexus-text-sm);
  font-weight: 600;
  color: var(--nexus-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdownOrgActions {
  display: flex;
  gap: var(--nexus-space-sm);
  padding-left: calc(22px + var(--nexus-space-sm));
}

.dropdownOrgAction {
  font-family: var(--nexus-font-sans);
  font-size: 12px;
  color: var(--nexus-text-muted);
  text-decoration: none;
  padding: 2px var(--nexus-space-xs);
  border-radius: var(--nexus-radius-xs);
  transition: background var(--nexus-transition-fast), color var(--nexus-transition-fast);
}

.dropdownOrgAction:hover {
  background: var(--nexus-surface-hover);
  color: var(--nexus-text);
}

/* ── Dark-mode switch (phone widths only — wider bars show their own toggle) ── */
.themeRow {
  display: none;
}

.themeLabel {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-sm);
}

@media (max-width: 768px) {
  .themeRow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--nexus-space-sm) var(--nexus-space-md);
    margin-top: var(--nexus-space-xs);
    border-top: 1px solid var(--nexus-border);
    padding-top: var(--nexus-space-sm);
    font-size: var(--nexus-text-sm);
    color: var(--nexus-text-secondary);
  }

  .dropdown {
    max-height: calc(100dvh - 64px);
    overflow-y: auto;
  }
}

/* ── Teams: name over a muted game / role line ── */
.dropdownTeam {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--nexus-space-sm) var(--nexus-space-md);
  text-decoration: none;
  color: var(--nexus-text);
  border-radius: var(--nexus-radius-control);
}

.dropdownTeam:hover {
  background: var(--nexus-overlay-light);
}

.dropdownTeamName {
  font-size: var(--nexus-text-sm);
  font-weight: var(--nexus-weight-medium);
}

.dropdownTeamMeta {
  font-size: var(--nexus-text-xs);
  color: var(--nexus-text-muted);
}
